import { Box, Divider, Skeleton, type SxProps, type Theme, Typography } from "@mui/material";
import { type FC, Fragment, type ReactNode } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL, SURFACE } from "../../../theme/tokens";

export type StatStripItem = {
	/** Already-translated caption, rendered as a small uppercase label above the value. */
	label: string;
	/** Pre-formatted value ("36,741.29", "97%", "12") — formatting stays caller-owned so the strip is unit-agnostic. */
	value: ReactNode;
	/** Hero metric: renders the value larger + in the brand color, for the one figure that leads the group (band variant). */
	emphasis?: boolean;
	/**
	 * Muted breakdown line under the value ("9 auto-created · 5 manual"), already translated. Stacked layout only —
	 * a row-layout strip is one line by design, so it ignores this.
	 */
	caption?: ReactNode;
	dataTestId?: string;
};

/**
 * `inline` (default) — the compact toolbar pill sized to sit beside 40px controls.
 * `band` — a full-width summary panel: cells stretch to equal shares with larger figures, for a
 * prominent header/summary row above a grid where the KPIs are the focus, not a toolbar accessory.
 */
export type StatStripVariant = "inline" | "band";

/**
 * `stacked` (default) — small uppercase label above the value; the presentation for a strip that
 * owns its own vertical space.
 * `row` — value then label on ONE baseline-aligned line, so the strip fits a 40px control row
 * (a grid toolbar) without stacking two type sizes into it.
 */
export type StatStripLayout = "stacked" | "row";

type Props = {
	items: StatStripItem[];
	/** Optional trailing segment inside the strip (divider-separated) — e.g. a breakdown-popover trigger. */
	endSlot?: ReactNode;
	variant?: StatStripVariant;
	layout?: StatStripLayout;
	/**
	 * Strip fill. `muted` (default) recedes into a toolbar; `paper` is white, for a strip that has to
	 * read as its own raised pill sitting ON a muted page background rather than a well inside a panel.
	 */
	surface?: "muted" | "paper";
	/**
	 * `upper` (default) renders the caption as a small uppercase label — the KPI treatment.
	 * `sentence` renders it at the value's own size and colour so the pair reads as one PHRASE
	 * ("11 people to add") instead of a figure plus a shouted label. Only meaningful with
	 * `layout="row"`, where value and label share a line.
	 */
	labelCase?: "upper" | "sentence";
	/**
	 * Skeleton every value (and caption) while the figures load, keeping the labels and the strip's shape — rather
	 * than showing provisional zeros a reader would take as the answer.
	 */
	loading?: boolean;
	/**
	 * Band variant: a cell never shrinks below its own content — when the row runs out of room the cells wrap onto a
	 * new row instead of running into each other. For bands whose cells carry long values or captions.
	 */
	wrapCells?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/** Placeholder widths (px) while loading — roughly a short figure, and a short breakdown line. */
const SKELETON_VALUE_WIDTH = 56;
const SKELETON_CAPTION_WIDTH = 120;

// Mount fade + tiny lift (transform/opacity only); dropped entirely under reduced motion.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes statStripEnter": {
		from: { opacity: 0, transform: "translateY(4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `statStripEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const dividerSx = (variant: StatStripVariant): SxProps<Theme> => ({ borderColor: NEUTRAL[200], my: variant === "band" ? 1 : 0.5 });

/** Row-layout pill height — matches the app's small controls (tab pills, buttons) so it aligns in a control row. */
const CONTROL_HEIGHT_PX = 40;

/**
 * KPI strip: a row of label-over-value figures grouped on one soft surface with hairline dividers —
 * the aggregation presentation where a plain text run of "Label: value · Label: value" reads as noise.
 * Values use tabular numerals so figures align, and it wraps internally when cramped. `variant="inline"`
 * (default) is a compact pill sized to sit beside 40px toolbar controls; `variant="band"` is a full-width
 * summary panel with equal-share cells and larger figures for a prominent header row above a grid.
 */
export const StatStrip: FC<Props> = ({
	items,
	endSlot,
	variant = "inline",
	layout = "stacked",
	surface = "muted",
	labelCase = "upper",
	// DEFAULT: not loading — every existing strip passes ready figures.
	loading = false,
	// DEFAULT: off — existing bands keep their equal-share cells exactly as they lay out today.
	wrapCells = false,
	dataTestId,
	sx,
}) => {
	const isBand = variant === "band";
	const isRow = layout === "row";
	const backgroundColor = surface === "paper" ? SURFACE : NEUTRAL[50];
	const isSentenceLabel = labelCase === "sentence";
	return (
		<Box
			data-testid={dataTestId}
			sx={[
				ENTRANCE_SX,
				isBand
					? {
							display: "flex",
							width: "100%",
							alignItems: "stretch",
							flexWrap: "wrap",
							rowGap: 1,
							backgroundColor,
							border: `1px solid ${NEUTRAL[200]}`,
							borderRadius: 2,
							px: 1,
							py: 1.25,
						}
					: {
							display: "inline-flex",
							alignItems: "stretch",
							flexWrap: "wrap",
							rowGap: 0.5,
							backgroundColor,
							border: `1px solid ${NEUTRAL[200]}`,
							// Match the 8px control radius (SystemButton/inputs) so the strip sits in a toolbar row natively.
							borderRadius: 1,
							// Row layout is a single line of figures, so the pill takes a control's height + padding
							// instead of the hugging 1px it needs when each cell stacks its own two lines.
							...(isRow ? { alignItems: "center", minHeight: CONTROL_HEIGHT_PX, px: 1 } : { px: 0.5, py: "1px" }),
						},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{items.map((item, index) => {
				const label = (
					<Typography
						component="span"
						sx={
							isSentenceLabel
								? // Reads as the tail of the value's own sentence: same size, same ink, no shouting.
									{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, lineHeight: 1.2, color: INK.PRIMARY, whiteSpace: "nowrap" }
								: {
										fontSize: FONT_SIZE.LABEL_XS,
										fontWeight: 600,
										letterSpacing: "0.06em",
										textTransform: "uppercase",
										lineHeight: 1.2,
										// Band labels carry more weight (darker) so the panel doesn't read as washed gray.
										color: isBand ? INK.SECONDARY : INK.MUTED,
										whiteSpace: "nowrap",
									}
						}
					>
						{item.label}
					</Typography>
				);
				const value = (
					<Typography
						component="span"
						sx={{
							fontSize: isBand
								? item.emphasis
									? FONT_SIZE.TITLE_XXL
									: FONT_SIZE.TITLE_XL
								: isRow
									? item.emphasis
										? FONT_SIZE.TITLE_MD
										: FONT_SIZE.EMPHASIS
									: FONT_SIZE.TITLE_SM,
							fontWeight: isBand ? 800 : item.emphasis ? 800 : 700,
							lineHeight: 1.2,
							color: item.emphasis ? "primary.dark" : INK.PRIMARY,
							fontVariantNumeric: "tabular-nums",
							whiteSpace: "nowrap",
						}}
					>
						{loading ? (
							<Skeleton variant="text" sx={{ width: SKELETON_VALUE_WIDTH }} data-testid={item.dataTestId ? `${item.dataTestId}-skeleton` : undefined} />
						) : (
							item.value
						)}
					</Typography>
				);
				return (
					<Fragment key={item.label}>
						{index > 0 && <Divider orientation="vertical" flexItem sx={dividerSx(variant)} />}
						<Box
							data-testid={item.dataTestId}
							sx={{
								display: "flex",
								// Row layout leads with the figure and trails the label on one baseline, so the
								// strip reads as a figure run in a control row instead of two stacked type sizes.
								...(isRow
									? { flexDirection: "row", alignItems: "baseline", gap: 0.75, px: 1.25 }
									: {
											flexDirection: "column",
											justifyContent: "center",
											gap: isBand ? "3px" : "1px",
											// Band cells stretch to equal shares so the summary fills its row; inline hugs content.
											...(isBand ? { flex: "1 1 0", minWidth: wrapCells ? "max-content" : 120, px: 2.5, py: 0.25 } : { px: 1.5, py: "1px" }),
										}),
							}}
						>
							{isRow ? (
								<>
									{value}
									{label}
								</>
							) : (
								<>
									{label}
									{value}
									{item.caption && (
										<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.3, color: INK.MUTED, whiteSpace: "nowrap" }}>
											{loading ? <Skeleton variant="text" sx={{ width: SKELETON_CAPTION_WIDTH }} /> : item.caption}
										</Typography>
									)}
								</>
							)}
						</Box>
					</Fragment>
				);
			})}
			{endSlot && (
				<>
					<Divider orientation="vertical" flexItem sx={dividerSx(variant)} />
					<Box sx={{ display: "flex", alignItems: "center", px: isBand ? 1 : 0.5 }}>{endSlot}</Box>
				</>
			)}
		</Box>
	);
};
